import { useCallback, useContext, useRef, useState, createContext, type ReactNode } from 'react';

type SoundType = 'buzzer' | 'buzz-won' | 'correct' | 'incorrect' | 'tick' | 'steal';

interface SoundManager {
  play: (sound: SoundType) => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  volume: number;
  setVolume: (volume: number) => void;
}

const SoundContext = createContext<SoundManager | null>(null);

export const SoundProvider = ({ children }: { children: ReactNode }) => {
  const [enabled, setEnabled] = useState(true);
  const [volume, setVolume] = useState(0.3);
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    return audioContextRef.current;
  }, []);

  const playTone = useCallback((
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volumeLevel: number = volume
  ) => {
    if (!enabled) return;
    
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      gainNode.gain.setValueAtTime(volumeLevel, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [enabled, volume, getAudioContext]);

  const playSequence = useCallback((frequencies: number[], duration: number) => {
    if (!enabled) return;
    
    frequencies.forEach((freq, i) => {
      setTimeout(() => playTone(freq, duration, 'sine', volume), i * (duration * 1000));
    });
  }, [enabled, playTone, volume]);

  const play: SoundManager['play'] = useCallback((sound) => {
    if (!enabled) return;
    
    switch (sound) {
      case 'buzzer':
        playTone(440, 0.3, 'square', volume * 0.5);
        break;
      case 'buzz-won':
        playSequence([523, 659, 784], 0.15);
        break;
      case 'correct':
        playSequence([523, 659, 784, 1047], 0.12);
        break;
      case 'incorrect':
        playTone(180, 0.4, 'sawtooth', volume * 0.4);
        break;
      case 'tick':
        playTone(1200, 0.05, 'square', volume * 0.2);
        break;
      case 'steal':
        playSequence([440, 0, 440, 0, 880], 0.2);
        break;
    }
  }, [enabled, volume, playTone, playSequence]);

  return (
    <SoundContext.Provider value={{ play, enabled, setEnabled, volume, setVolume }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = (): SoundManager => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within SoundProvider');
  }
  return context;
};
